import * as reader from "@vp/ab-reader"
import VistaprintAuth from "@vp/auth"
import { BOOKEND_TYPES, Footer, Header } from "@vp/esi-bookends"
import * as fido from "@vp/fido"
import { latestPricingContextModuleUrl } from "@vp/pricing-context-module/cdn"
import pricingContextModuleHelpers from "@vp/pricing-context-module/helpers"
import NewRelic from "@vp/react-new-relic-browser"
import {
  BoundedContent,
  Column,
  GridContainer,
  H1,
  MainContentStart,
  Row,
  SiteContent,
  SiteMain,
  Box,
} from "@vp/swan";
import React, { useEffect, useMemo } from "react"
import { Helmet } from "react-helmet"
import { AddendumSection } from "../components/AddendumSection"
import Category from "../components/Category"
import { useTracking } from "../helpers/trackingHooks"
import { SwanConfiguration } from "./SwanConfiguration"

const AllProducts = props => {
  useEffect(() => {
    fido.init()
  }, [])

  useEffect(() => {
    VistaprintAuth.init(
      {
        options: { requireSession: true },
        developmentMode: process.env.NODE_ENV === "development" ? true : false,
        culture: props.pageContext.locale,
      },
      () => {
        const pricingContextConfig = {
          merchantId: props.pageContext.tenant,
          market: props.pageContext.country,
          isDevelopmentMode:
            process.env.NODE_ENV === "development" ? true : false,
        }
        pricingContextModuleHelpers.initializePricingContext(
          pricingContextConfig,
          () => {}
        )
      }
    )
  }, [
    props.pageContext.tenant,
    props.pageContext.country,
    props.pageContext.locale,
  ])

  reader.initialize()

  const trackingConfiguration = useMemo(() => ({
    pageSection: "Product List",
    pageStage: "Discovery",
    pageName: "All Products:Product List",
    genTrack: "Sprinkles",
    languageLocale: props.pageContext.locale,
  }), [props.pageContext.locale])

  const tracking = useTracking(
    props.pageContext.segmentKey,
    props.pageContext.tenant,
    props.pageContext.locale
  )

  useEffect(() => {
    if (tracking) {
      tracking.page({ ...trackingConfiguration })
    }
  }, [tracking, trackingConfiguration])

  const { title, addendumSection, seo } = props.pageContext.pageData.pageAllProductsPage
  const { metaTitle, metaRobot, metaDescription } = seo || {}
  const categories = props.pageContext.pageData.Categories

  return (
    <SwanConfiguration locale={props.pageContext.locale} tenant={props.pageContext.tenant}>
      <SiteContent>
        <NewRelic newRelicApplicationId={process.env.GATSBY_NEW_RELIC_APPLICATION_ID} />
        <Helmet>
          <html lang={props.pageContext.locale} />
          {metaTitle && <title>{metaTitle}</title>}
          {metaRobot && <meta name="ROBOTS" content={metaRobot} />}
          {metaDescription && (
            <meta name="description" content={metaDescription} />
          )}
          <meta name="theme-color" content="#006196" />
          <script defer src={latestPricingContextModuleUrl}></script>
        </Helmet>
        <Header
          locale={props.pageContext.locale}
          tenant={props.pageContext.tenant}
          type={BOOKEND_TYPES.FULL}
          hideSearch={false}
          hideVat={false}
          trackingConfiguration={trackingConfiguration}
        />
        <SiteMain>
          <MainContentStart />
          <BoundedContent>
            <GridContainer mt={"5"}>
              <Box mb="7">
                <Row>
                  <Column span={12}>
                    <H1 fontSize={"x3large"}>{title}</H1>
                  </Column>
                </Row>
              </Box>
              {categories.map((category, index) => (
                <Category
                  key={`category_${index}`}
                  category={category}
                />
              ))}
              <AddendumSection addendumSection={addendumSection} />
            </GridContainer>
          </BoundedContent>
        </SiteMain>
        <Footer
          locale={props.pageContext.locale}
          tenant={props.pageContext.tenant}
          type={BOOKEND_TYPES.FULL}
          trackingConfiguration={trackingConfiguration}
        />
      </SiteContent>
    </SwanConfiguration>
  )
}

export default AllProducts
